import React, { createContext, useContext, useMemo } from 'react';
import { getUrlParam } from 'utils/url';
import { FeatureFlagId, QUERY_FF_PREFIX } from 'types/featureflags';
import { useServerFeatureFlags } from 'api/featureflags';
import { FEATURE_FLAGS_DEFAULTS } from 'featureFlagsDefaults';
import { getEnv } from 'utils/api';

type FeatureFlagsState = Record<FeatureFlagId, boolean>;

interface FeatureFlagsContextValue {
	flags: FeatureFlagsState;
	isFeatureFlagActive: (id: FeatureFlagId) => boolean;
	ready: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
	undefined
);

export const FeatureFlagsProvider: React.FC<React.PropsWithChildren> = ({
	children = null
}) => {
	const { data: serverFeatureFlags } = useServerFeatureFlags();

	const flags = useMemo(() => {
		const calculatedFlags = {} as FeatureFlagsState;

		(Object.values(FeatureFlagId) as FeatureFlagId[]).forEach((flag) => {
			const urlParamKey = `${QUERY_FF_PREFIX}:${flag}`;
			const urlParamValue = getUrlParam(urlParamKey);

			let isActive = !!FEATURE_FLAGS_DEFAULTS[flag].env[getEnv()];

			if (serverFeatureFlags?.features?.[flag] !== undefined) {
				isActive = !!serverFeatureFlags.features[flag];
			}

			if (urlParamValue === 'true' || urlParamValue === 'false') {
				isActive = urlParamValue === 'true';
			}

			calculatedFlags[flag] = isActive;
		});

		return calculatedFlags;
	}, [serverFeatureFlags]);

	const isFeatureFlagActive = React.useCallback(
		(id: FeatureFlagId) => !!flags[id],
		[flags]
	);

	const value = useMemo(
		() => ({ flags, isFeatureFlagActive, ready: !!serverFeatureFlags }),
		[flags, isFeatureFlagActive, serverFeatureFlags]
	);

	return (
		<FeatureFlagsContext.Provider value={value}>
			{children}
		</FeatureFlagsContext.Provider>
	);
};

const useFeatureFlagsContext = () => {
	const context = useContext(FeatureFlagsContext);
	/* istanbul ignore if: no need to test this */
	if (context === undefined) {
		throw new Error(
			'useFeatureFlagsContext must be used within a FeatureFlagsProvider'
		);
	}
	return context;
};

export const useFeatureFlags = () => {
	const { isFeatureFlagActive, ready } = useFeatureFlagsContext();

	return {
		isFeatureFlagActive,
		ready
	};
};
